import { useState, useEffect } from 'react';
import { log } from '@/lib/logger';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Label } from '@/components/ui/form/label';
import { Input } from '@/components/ui/form/input';
import { Switch } from '@/components/ui/form/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/form/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/feedback/alert-dialog';
import { Shield, Lock, Eye, EyeOff, Loader2, Trash2, Mail, Save } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { auth } from '@/lib/auth';
import { useAuth } from '@/context/AuthContext';
import { fetchPrivacySettings, updatePrivacySettings, changePassword, changeEmail } from '@/lib/api';

export function PrivacySecuritySection() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [showEmailPassword, setShowEmailPassword] = useState(false);
  const [isChangingEmail, setIsChangingEmail] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [privacySettings, setPrivacySettings] = useState({ profileVisibility: 'private' as 'public' | 'private' | 'coaches', shareActivityData: false, shareTrainingMetrics: false });
  const [isSavingPrivacy, setIsSavingPrivacy] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteAccountData, setDeleteAccountData] = useState({ password: '', confirmation: '' });
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);

  useEffect(() => {
    const loadPrivacySettings = async () => {
      try {
        const settings = await fetchPrivacySettings();
        setPrivacySettings({ profileVisibility: settings.profile_visibility, shareActivityData: settings.share_activity_data, shareTrainingMetrics: settings.share_training_metrics });
      } catch (error) { log.error('SETTINGS', 'Failed to load privacy settings:', error); }
      finally { setIsLoading(false); }
    };
    loadPrivacySettings();
  }, []);

  const handleEmailChange = async () => {
    if (!newEmail || !emailPassword) { toast({ title: 'Missing fields', description: 'Please fill in both email and password fields', variant: 'destructive' }); return; }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) { toast({ title: 'Invalid email', description: 'Please enter a valid email address', variant: 'destructive' }); return; }
    if (newEmail === user?.email) { toast({ title: 'Email unchanged', variant: 'destructive' }); return; }
    setIsChangingEmail(true);
    try {
      await changeEmail({ new_email: newEmail, password: emailPassword });
      toast({ title: 'Email changed', description: 'Your email has been updated. Please log in with your new email.' });
      setNewEmail(''); setEmailPassword('');
      window.location.reload();
    } catch (error) {
      const apiError = error as { status?: number; message?: string };
      let msg = 'Could not update email';
      if (apiError.status === 401) msg = 'Incorrect password';
      else if (apiError.status === 409) msg = 'This email is already in use';
      else if (apiError.message) msg = apiError.message;
      toast({ title: 'Failed to change email', description: msg, variant: 'destructive' });
    } finally { setIsChangingEmail(false); }
  };

  const handlePasswordChange = async () => {
    if (!passwordData.currentPassword || !passwordData.newPassword) { toast({ title: 'Missing fields', variant: 'destructive' }); return; }
    if (passwordData.newPassword.length < 8) { toast({ title: 'Password too short', description: 'Minimum 8 characters', variant: 'destructive' }); return; }
    if (passwordData.newPassword !== passwordData.confirmPassword) { toast({ title: 'Passwords do not match', variant: 'destructive' }); return; }
    setIsChangingPassword(true);
    try {
      await changePassword({ current_password: passwordData.currentPassword, new_password: passwordData.newPassword, confirm_password: passwordData.confirmPassword });
      toast({ title: 'Password changed' });
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) { toast({ title: 'Failed to change password', description: error instanceof Error ? error.message : 'Could not update', variant: 'destructive' }); }
    finally { setIsChangingPassword(false); }
  };

  const handlePrivacySave = async () => {
    setIsSavingPrivacy(true);
    try {
      await updatePrivacySettings({ profile_visibility: privacySettings.profileVisibility, share_activity_data: privacySettings.shareActivityData, share_training_metrics: privacySettings.shareTrainingMetrics });
      toast({ title: 'Privacy settings updated' });
    } catch (error) { toast({ title: 'Failed to save', description: error instanceof Error ? error.message : 'Could not save', variant: 'destructive' }); }
    finally { setIsSavingPrivacy(false); }
  };

  const handleDeleteAccount = async () => {
    if (!deleteAccountData.password) { toast({ title: 'Password required', variant: 'destructive' }); return; }
    if (deleteAccountData.confirmation !== 'DELETE') { toast({ title: 'Type DELETE to confirm', variant: 'destructive' }); return; }
    setIsDeletingAccount(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      auth.clear();
      toast({ title: 'Account deleted' });
      navigate('/', { replace: true });
    } catch (error) { toast({ title: 'Failed', description: error instanceof Error ? error.message : 'Could not delete', variant: 'destructive' }); setIsDeletingAccount(false); }
  };

  const hudInputClass = 'bg-white/[0.03] border-white/[0.1] font-mono text-sm focus:border-[#00FFC8]/30 focus:ring-[#00FFC8]/20';
  const hudBtnClass = cn(
    'flex items-center justify-center gap-2 px-4 py-2 rounded-md font-mono text-[10px] tracking-[0.12em] uppercase transition-all duration-200 border',
    'border-[rgba(0,255,200,0.3)] bg-[rgba(0,255,200,0.08)] text-[#00FFC8] hover:bg-[rgba(0,255,200,0.15)] hover:shadow-[0_0_16px_rgba(0,255,200,0.15)]'
  );
  const hudBtnDisabled = 'border-white/[0.06] bg-white/[0.02] text-white/30 cursor-not-allowed';

  return (
    <div className="rounded-lg border border-white/[0.08] bg-[rgba(12,16,24,0.85)] backdrop-blur-[16px] p-5 shadow-[inset_0_1px_0_rgba(0,255,200,0.04)]">
      <div className="flex items-center gap-2.5 mb-6">
        <div className="p-1.5 rounded-md border border-[#00FFC8]/20 bg-[#00FFC8]/5">
          <Shield className="h-4 w-4 text-[#00FFC8]" />
        </div>
        <div>
          <h3 className="text-sm font-medium text-foreground">Privacy & Security</h3>
          <p className="text-[9px] font-mono tracking-[0.15em] uppercase text-muted-foreground">ACCESS CONTROL</p>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
      ) : (
        <div className="space-y-6">
          {/* Change Email */}
          <div className="space-y-3">
            <span className="text-[9px] font-mono tracking-[0.15em] uppercase text-white/30 block">CHANGE EMAIL</span>
            <div className="space-y-2">
              <Label className="text-[10px] font-mono text-[#A0A0A0]">Current Email</Label>
              <Input value={user?.email || ''} disabled className={cn(hudInputClass, 'opacity-50')} />
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] font-mono text-[#A0A0A0]">New Email</Label>
              <Input type="email" placeholder="new@example.com" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} className={hudInputClass} disabled={isChangingEmail} />
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] font-mono text-[#A0A0A0]">Password</Label>
              <div className="relative">
                <Input type={showEmailPassword ? 'text' : 'password'} placeholder="Confirm with password" value={emailPassword} onChange={(e) => setEmailPassword(e.target.value)} className={hudInputClass} disabled={isChangingEmail} />
                <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60" onClick={() => setShowEmailPassword(!showEmailPassword)}>
                  {showEmailPassword ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                </button>
              </div>
            </div>
            <button onClick={handleEmailChange} disabled={isChangingEmail || !newEmail || !emailPassword} className={cn(isChangingEmail || !newEmail || !emailPassword ? hudBtnDisabled : hudBtnClass, 'w-full py-2.5 rounded-md font-mono text-[10px] tracking-[0.12em] uppercase border flex items-center justify-center gap-2')}>
              {isChangingEmail ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> UPDATING...</> : <><Mail className="h-3.5 w-3.5" /> UPDATE EMAIL</>}
            </button>
          </div>

          <div className="border-t border-white/[0.06]" />

          {/* Change Password */}
          <div className="space-y-3">
            <span className="text-[9px] font-mono tracking-[0.15em] uppercase text-white/30 block">CHANGE PASSWORD</span>
            {[
              { id: 'current', label: 'Current Password', value: passwordData.currentPassword, show: showCurrentPassword, toggle: () => setShowCurrentPassword(!showCurrentPassword), onChange: (v: string) => setPasswordData({ ...passwordData, currentPassword: v }) },
              { id: 'new', label: 'New Password', value: passwordData.newPassword, show: showNewPassword, toggle: () => setShowNewPassword(!showNewPassword), onChange: (v: string) => setPasswordData({ ...passwordData, newPassword: v }) },
              { id: 'confirm', label: 'Confirm Password', value: passwordData.confirmPassword, show: showConfirmPassword, toggle: () => setShowConfirmPassword(!showConfirmPassword), onChange: (v: string) => setPasswordData({ ...passwordData, confirmPassword: v }) },
            ].map((field) => (
              <div key={field.id} className="space-y-2">
                <Label className="text-[10px] font-mono text-[#A0A0A0]">{field.label}</Label>
                <div className="relative">
                  <Input type={field.show ? 'text' : 'password'} value={field.value} onChange={(e) => field.onChange(e.target.value)} className={hudInputClass} />
                  <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60" onClick={field.toggle}>
                    {field.show ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                  </button>
                </div>
              </div>
            ))}
            <button onClick={handlePasswordChange} disabled={isChangingPassword || !passwordData.currentPassword || !passwordData.newPassword} className={cn(isChangingPassword || !passwordData.currentPassword || !passwordData.newPassword ? hudBtnDisabled : hudBtnClass, 'w-full py-2.5 rounded-md font-mono text-[10px] tracking-[0.12em] uppercase border flex items-center justify-center gap-2')}>
              {isChangingPassword ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> UPDATING...</> : <><Lock className="h-3.5 w-3.5" /> UPDATE PASSWORD</>}
            </button>
          </div>

          <div className="border-t border-white/[0.06]" />

          {/* Privacy */}
          <div className="space-y-3">
            <span className="text-[9px] font-mono tracking-[0.15em] uppercase text-white/30 block">PRIVACY SETTINGS</span>
            <div className="space-y-2">
              <Label className="text-[10px] font-mono text-[#A0A0A0]">Profile Visibility</Label>
              <Select value={privacySettings.profileVisibility} onValueChange={(value) => setPrivacySettings({ ...privacySettings, profileVisibility: value as 'public' | 'private' | 'coaches' })}>
                <SelectTrigger className={cn(hudInputClass, 'font-mono text-sm')}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[rgba(12,16,24,0.97)] backdrop-blur-[20px] border-white/[0.12] z-50">
                  <SelectItem value="private" className="font-mono text-sm">Private — Only you</SelectItem>
                  <SelectItem value="coaches" className="font-mono text-sm">Coaches — Only your coaches</SelectItem>
                  <SelectItem value="public" className="font-mono text-sm">Public — Everyone</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {[
              { key: 'shareActivityData', label: 'Share Activity Data', desc: 'Anonymized activity data for research' },
              { key: 'shareTrainingMetrics', label: 'Share Training Metrics', desc: 'Training metrics with coaches' },
            ].map((item) => (
              <div key={item.key} className="flex items-center justify-between px-3 py-2.5 rounded-md border border-white/[0.04] bg-white/[0.01]">
                <div>
                  <Label htmlFor={item.key} className="cursor-pointer text-sm text-foreground">{item.label}</Label>
                  <p className="text-[10px] font-mono text-[#A0A0A0]">{item.desc}</p>
                </div>
                <Switch id={item.key} checked={privacySettings[item.key as keyof typeof privacySettings] as boolean} onCheckedChange={(checked) => setPrivacySettings({ ...privacySettings, [item.key]: checked })} />
              </div>
            ))}
            <button onClick={handlePrivacySave} disabled={isSavingPrivacy} className={cn(isSavingPrivacy ? hudBtnDisabled : hudBtnClass, 'w-full py-2.5 rounded-md font-mono text-[10px] tracking-[0.12em] uppercase border flex items-center justify-center gap-2')}>
              {isSavingPrivacy ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> SAVING...</> : <><Save className="h-3.5 w-3.5" /> COMMIT PRIVACY</>}
            </button>
          </div>

          <div className="border-t border-white/[0.06]" />

          {/* Danger Zone */}
          <div className="space-y-3">
            <span className="text-[9px] font-mono tracking-[0.15em] uppercase text-[hsl(0,70%,55%)] block">DANGER ZONE</span>
            <p className="text-[10px] font-mono text-[#A0A0A0]">Permanently delete your account and all associated data</p>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button disabled={isDeletingAccount} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-md font-mono text-[10px] tracking-[0.12em] uppercase transition-all duration-200 border border-[hsl(0,70%,55%)]/30 bg-[hsl(0,70%,55%)]/[0.08] text-[hsl(0,70%,55%)] hover:bg-[hsl(0,70%,55%)]/15 disabled:opacity-40">
                  {isDeletingAccount ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> DELETING...</> : <><Trash2 className="h-3.5 w-3.5" /> DELETE ACCOUNT</>}
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-[rgba(12,16,24,0.97)] backdrop-blur-[20px] border-white/[0.12]">
                <AlertDialogHeader>
                  <AlertDialogTitle className="font-mono">Are you absolutely sure?</AlertDialogTitle>
                  <AlertDialogDescription className="space-y-4">
                    <p className="text-sm">This action cannot be undone. All your data will be permanently deleted.</p>
                    <div className="space-y-3 pt-2">
                      <div className="space-y-2">
                        <Label className="text-[10px] font-mono text-[#A0A0A0]">Enter your password</Label>
                        <Input type="password" placeholder="Password" value={deleteAccountData.password} onChange={(e) => setDeleteAccountData({ ...deleteAccountData, password: e.target.value })} className={hudInputClass} disabled={isDeletingAccount} />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-[10px] font-mono text-[#A0A0A0]">Type "DELETE" to confirm</Label>
                        <Input type="text" placeholder="Type DELETE" value={deleteAccountData.confirmation} onChange={(e) => setDeleteAccountData({ ...deleteAccountData, confirmation: e.target.value })} className={hudInputClass} disabled={isDeletingAccount} />
                      </div>
                    </div>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="font-mono text-[10px] tracking-wider" onClick={() => setDeleteAccountData({ password: '', confirmation: '' })}>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDeleteAccount} disabled={!deleteAccountData.password || deleteAccountData.confirmation !== 'DELETE' || isDeletingAccount} className="bg-destructive text-destructive-foreground hover:bg-destructive/90 font-mono text-[10px] tracking-wider disabled:opacity-50">Delete Account</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      )}
    </div>
  );
}
